import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { Field, TextInput, TextArea, SelectInput, ImageUpload, AdminModal, StatusPill } from "@/admin/adminUi";

const EMPTY = { name: "", slug: "", tagline: "", description: "", positioning: "", status: "active", image: "", cta_label: "", cta_url: "", industry: "", order: 0, published: true };

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/products").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...editing, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/products/${editing.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this system?")) return;
    await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="display text-3xl font-light text-oraya-mist">Systems</h1><p className="text-oraya-slate mt-1">Products of the ORAYA ecosystem.</p></div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="add-product" className="inline-flex items-center gap-2 rounded-lg bg-oraya-mist text-oraya-black px-4 py-2.5 text-sm font-medium hover:bg-white">
          <Plus className="w-4 h-4" /> New system
        </button>
      </div>

      <div className="bg-oraya-graphite border border-white/[0.06] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-oraya-slate border-b border-white/[0.06]">
            <th className="px-6 py-4 font-medium">Name</th><th className="px-6 py-4 font-medium hidden md:table-cell">Industry</th>
            <th className="px-6 py-4 font-medium">Status</th><th className="px-6 py-4 font-medium text-right">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-white/[0.04] text-oraya-mist" data-testid={`product-row-${p.slug}`}>
                <td className="px-6 py-4"><div className="font-medium">{p.name}</div><div className="text-oraya-slate text-xs">{p.tagline}</div></td>
                <td className="px-6 py-4 hidden md:table-cell text-oraya-slate">{p.industry || "—"}</td>
                <td className="px-6 py-4"><StatusPill status={p.status} /></td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => setEditing({ ...p })} data-testid={`edit-product-${p.slug}`} className="text-oraya-slate hover:text-oraya-mist p-2"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} data-testid={`delete-product-${p.slug}`} className="text-oraya-slate hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="4" className="px-6 py-10 text-center text-oraya-slate">No systems yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <AdminModal title={editing.id ? "Edit system" : "New system"} onClose={() => setEditing(null)} onSave={save} saving={saving} testid="product-modal">
          <Field label="Name"><TextInput value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="product-name" /></Field>
          <Field label="Slug (optional)"><TextInput value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} placeholder="auto from name" testid="product-slug" /></Field>
          <Field label="Tagline"><TextInput value={editing.tagline} onChange={(v) => setEditing({ ...editing, tagline: v })} testid="product-tagline" /></Field>
          <Field label="Description"><TextArea value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="product-description" /></Field>
          <Field label="Positioning"><TextInput value={editing.positioning} onChange={(v) => setEditing({ ...editing, positioning: v })} testid="product-positioning" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Status"><SelectInput value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })} testid="product-status" options={[{ value: "active", label: "Active" }, { value: "coming_soon", label: "Coming Soon" }, { value: "future", label: "Future" }]} /></Field>
            <Field label="Industry"><TextInput value={editing.industry} onChange={(v) => setEditing({ ...editing, industry: v })} testid="product-industry" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="CTA label"><TextInput value={editing.cta_label} onChange={(v) => setEditing({ ...editing, cta_label: v })} testid="product-cta-label" /></Field>
            <Field label="CTA url / route"><TextInput value={editing.cta_url} onChange={(v) => setEditing({ ...editing, cta_url: v })} placeholder="/systems/..." testid="product-cta-url" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Order"><TextInput type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} testid="product-order" /></Field>
            <Field label="Published"><SelectInput value={String(editing.published)} onChange={(v) => setEditing({ ...editing, published: v === "true" })} testid="product-published" options={[{ value: "true", label: "Published" }, { value: "false", label: "Hidden" }]} /></Field>
          </div>
          <Field label="Image"><ImageUpload value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} testid="product-image" /></Field>
        </AdminModal>
      )}
    </div>
  );
}
